/**
 * ================================================================
 * FIREBASE CONFIGURATION
 * ================================================================
 *
 * @fileOverview     Firebase configuration and initialization
 * @module           lib/firebase/config
 * @version          1.0.0
 * @since            2025-08-11
 * @author           Developer <dev@domain.com>
 *
 * @purpose          Initialize Firebase app and FCM messaging service
 * @dependencies     Firebase SDK, environment variables
 * @exports          Firebase app instance and messaging service
 *
 * @architecture
 * - Design pattern: Singleton pattern for Firebase app
 * - Configuration: Environment-based configuration
 * - Error handling: Graceful fallback when Firebase unavailable
 *
 * @security
 * - API keys: Stored in environment variables
 * - Domain verification: Firebase console domain configuration
 * - Permissions: FCM notification permissions handled separately
 *
 * ================================================================
 */

import { FirebaseApp, getApps, initializeApp } from 'firebase/app';
import { getMessaging, isSupported, Messaging } from 'firebase/messaging';

// ================================================================
// FIREBASE CONFIGURATION
// ================================================================
//
// Configuration object from Firebase console
// All values should be stored in environment variables
// Public configuration (no sensitive data)
// ================================================================

const firebaseConfig = {
  apiKey: import.meta.env.VITE_FIREBASE_API_KEY,
  authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN,
  projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID,
  storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID,
  appId: import.meta.env.VITE_FIREBASE_APP_ID,
  measurementId: import.meta.env.VITE_FIREBASE_MEASUREMENT_ID,
};

// ================================================================
// FIREBASE APP INITIALIZATION
// ================================================================
//
// Initialize Firebase app with singleton pattern
// Prevents multiple initialization attempts
// Provides safe access to Firebase services
// ================================================================

let firebaseApp: FirebaseApp | null = null;
let messaging: Messaging | null = null;

/**
 * Initialize Firebase application
 *
 * @returns {FirebaseApp | null} Firebase app instance or null if failed
 * @throws {Error} When Firebase configuration is invalid
 */
export function initializeFirebaseApp(): FirebaseApp | null {
  try {
    // ================================================================
    // CONFIGURATION VALIDATION
    // ================================================================
    //
    // Purpose: Ensure all required Firebase config values are present
    // Safety: Prevent initialization with incomplete configuration
    // Debugging: Log missing configuration for development
    // ================================================================

    const requiredEnvVars = [
      'VITE_FIREBASE_API_KEY',
      'VITE_FIREBASE_AUTH_DOMAIN',
      'VITE_FIREBASE_PROJECT_ID',
      'VITE_FIREBASE_MESSAGING_SENDER_ID',
      'VITE_FIREBASE_APP_ID',
    ];

    const missingVars = requiredEnvVars.filter((varName) => !import.meta.env[varName]);

    if (missingVars.length > 0) {
      console.warn('🔥 Firebase configuration incomplete. Missing:', missingVars);
      return null;
    }

    // ================================================================
    // APP INITIALIZATION
    // ================================================================
    //
    // Purpose: Initialize Firebase app if not already initialized
    // Singleton: Prevent duplicate initialization
    // Error handling: Graceful failure with logging
    // ================================================================

    if (!firebaseApp && getApps().length === 0) {
      firebaseApp = initializeApp(firebaseConfig);
      console.log('🔥 Firebase app initialized successfully');
    } else if (getApps().length > 0) {
      firebaseApp = getApps()[0];
    }

    return firebaseApp;
  } catch (error) {
    console.error('🔥 Firebase initialization failed:', error);
    return null;
  }
}

/**
 * Initialize Firebase Cloud Messaging
 *
 * @returns {Promise<Messaging | null>} FCM messaging instance or null
 * @async
 */
export async function initializeFirebaseMessaging(): Promise<Messaging | null> {
  try {
    // ================================================================
    // MESSAGING SUPPORT CHECK
    // ================================================================
    //
    // Purpose: Verify FCM is supported in current browser
    // Compatibility: Handle browsers without FCM support
    // Safety: Prevent errors in unsupported environments
    // ================================================================

    const messagingSupported = await isSupported();
    if (!messagingSupported) {
      console.warn('🔥 Firebase messaging not supported in this browser');
      return null;
    }

    // ================================================================
    // MESSAGING INITIALIZATION
    // ================================================================
    //
    // Purpose: Initialize FCM messaging service
    // Dependency: Requires Firebase app to be initialized first
    // Singleton: Return existing instance if available
    // ================================================================

    if (!firebaseApp) {
      firebaseApp = initializeFirebaseApp();
      if (!firebaseApp) {
        console.error('🔥 Cannot initialize messaging: Firebase app not available');
        return null;
      }
    }

    if (!messaging) {
      messaging = getMessaging(firebaseApp);
      console.log('🔥 Firebase messaging initialized successfully');
    }

    return messaging;
  } catch (error) {
    console.error('🔥 Firebase messaging initialization failed:', error);
    return null;
  }
}

/**
 * Get Firebase app instance
 *
 * @returns {FirebaseApp | null} Current Firebase app instance
 */
export function getFirebaseApp(): FirebaseApp | null {
  return firebaseApp || initializeFirebaseApp();
}

/**
 * Get Firebase messaging instance
 *
 * @returns {Promise<Messaging | null>} Current messaging instance
 * @async
 */
export async function getFirebaseMessaging(): Promise<Messaging | null> {
  return messaging || initializeFirebaseMessaging();
}

/**
 * Check if Firebase is properly configured
 *
 * @returns {boolean} True if Firebase configuration is complete
 */
export function isFirebaseConfigured(): boolean {
  const requiredVars = [
    'VITE_FIREBASE_API_KEY',
    'VITE_FIREBASE_AUTH_DOMAIN',
    'VITE_FIREBASE_PROJECT_ID',
    'VITE_FIREBASE_MESSAGING_SENDER_ID',
    'VITE_FIREBASE_APP_ID',
  ];

  return requiredVars.every((varName) => !!import.meta.env[varName]);
}

/**
 * ================================================================
 * END OF FILE: Firebase Configuration
 * ================================================================
 *
 * @exports          Firebase app and messaging initialization functions
 * @dependencies     Firebase SDK, environment variables
 * @usedBy           FCM service, notification hooks
 *
 * @maintenance
 * - Environment: Update .env files with Firebase project credentials
 * - Security: Verify Firebase console security rules
 * - Monitoring: Monitor Firebase quota and usage
 *
 * @setup
 * 1. Create Firebase project in console
 * 2. Enable Cloud Messaging
 * 3. Add web app configuration
 * 4. Set environment variables in .env files
 * 5. Configure domain in Firebase console
 *
 * ================================================================
 */
